<template>
  <div class="mr-page">
    <div class="mr-container">
      <div class="mr-query-band no-print">
        <h1 class="mr-main-title">{{ monthReportTitle }}</h1>
        <div class="mr-query-row">
          <span class="mr-query-label">查询月份</span>
          <el-date-picker
            v-model="yearMonth"
            type="month"
            value-format="yyyy-MM"
            placeholder="选择月份"
            size="small"
            class="mr-month-picker"
            @change="onMonthChange"
          />
          <el-button type="primary" size="small" class="mr-btn-export" :loading="exporting" @click="exportPdf">
            导出PDF
          </el-button>
        </div>
      </div>

      <div v-loading="loading" class="mr-report-wrap">
        <div v-if="report" id="reportContainer" ref="reportContainer" class="mr-report-body">
          <div class="mr-stats-row">
            <div class="mr-stat-card">
              <div class="mr-stat-label">期初库存(吨)</div>
              <div class="mr-stat-value">{{ fmtNum(report.beginStock) }}</div>
            </div>
            <div class="mr-stat-card">
              <div class="mr-stat-label">期末库存(吨)</div>
              <div class="mr-stat-value">{{ fmtNum(report.endStock) }}</div>
            </div>
            <div class="mr-stat-card">
              <div class="mr-stat-label">周期入库量(吨)</div>
              <div class="mr-stat-value">{{ fmtNum(report.inboundQty) }}</div>
            </div>
            <div class="mr-stat-card">
              <div class="mr-stat-label">周期出库量(吨)</div>
              <div class="mr-stat-value">{{ fmtNum(report.outboundQty) }}</div>
            </div>
            <div class="mr-stat-card">
              <div class="mr-stat-label">差异数量(吨)</div>
              <div
                class="mr-stat-value"
                :class="diffQtyClass(report.diffQty)"
              >
                {{ fmtDiffQty(report.diffQty) }}
              </div>
            </div>
            <div class="mr-stat-card">
              <div class="mr-stat-label">差异率(%)</div>
              <div
                class="mr-stat-value"
                :class="diffRateClass(report.diffRate)"
              >
                {{ fmtDiffRate(report.diffRate) }}
              </div>
            </div>
          </div>

          <div class="mr-compliance-card">
            <div class="mr-compliance-title">
              <i class="el-icon-success" v-if="report.compliant"></i>
              <i class="el-icon-warning" v-else></i>
              合规性结论
            </div>
            <div class="mr-compliance-content">
              <div>
                <span :class="report.compliant ? 'mr-pass' : 'mr-fail'">
                  {{ report.compliant ? '符合要求' : '超出标准' }}
                </span>
                <br >
                本月差异率为 {{ fmtRatePlain(report.diffRate) }}%，{{ report.compliant ? '满足' : '不满足' }}
                {{ report.complianceRegulation || '《仓储库存管理规范》第8.3条' }}
                规定（允许差异率绝对值 ≤ {{ stdPct }}%）。
              </div>
              <div class="mr-compliance-hint">
                {{
                  report.compliant
                    ? '库存管理状况良好，符合合规要求。'
                    : '请关注差异原因明细，及时调整盘点流程或加强管理。'
                }}
              </div>
            </div>
          </div>

          <div class="mr-card">
            <div class="mr-card-header">
              <span>货主差异数据</span>
            </div>
            <div class="mr-table-wrap">
              <table class="mr-table">
                <thead>
                  <tr>
                    <th>客户</th>
                    <th>期初库存(吨)</th>
                    <th>期末库存(吨)</th>
                    <th>差异数量(吨)</th>
                    <th>差异率(%)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!report.ownerDiffs || !report.ownerDiffs.length">
                    <td colspan="5" class="mr-td-center">无盘点数据，无法计算货主差异</td>
                  </tr>
                  <tr v-for="(od, idx) in report.ownerDiffs" :key="'o' + idx">
                    <td>{{ od.owner }}</td>
                    <td>{{ fmtNum(od.beginStock) }}</td>
                    <td>{{ fmtNum(od.endStock) }}</td>
                    <td :class="diffQtyClass(od.diffQty)">{{ fmtDiffQty(od.diffQty) }}</td>
                    <td>{{ fmtRatePlain(od.diffRate) }}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="mr-card">
            <div class="mr-card-header">
              <span>盘库记录</span>
            </div>
            <div class="mr-table-wrap">
              <table class="mr-table">
                <thead>
                  <tr>
                    <th>盘点单号</th>
                    <th>盘点时间</th>
                    <th>库位</th>
                    <th>客户</th>
                    <th>煤种</th>
                    <th>原库存(吨)</th>
                    <th>实盘(吨)</th>
                    <th>差异(吨)</th>
                    <th>差异率(%)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!countRecordRows.length">
                    <td colspan="9" class="mr-td-center">该月无盘点记录</td>
                  </tr>
                  <tr v-for="(row, i) in countRecordRows" :key="'cr' + i">
                    <td v-if="row._first" :rowspan="row._rowspan">{{ row._sheetNo }}</td>
                    <td v-if="row._first" :rowspan="row._rowspan">{{ row._countTime }}</td>
                    <td>{{ row.locationCode || row.locationName || '-' }}</td>
                    <td>{{ row.custName }}</td>
                    <td>{{ row.cargoName }}</td>
                    <td>{{ fmtNum(row.bookWeight) }}</td>
                    <td>{{ fmtNum(row.actualWeight) }}</td>
                    <td :class="diffQtyClass(row.diffWeight)">{{ fmtDiffQty(row.diffWeight) }}</td>
                    <td>{{ fmtRatePlain(row.diffRate) }}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="mr-card">
            <div class="mr-card-header">
              <span>差异原因明细</span>
            </div>
            <div v-if="!report.lines || !report.lines.length" class="mr-note">本月无盘点记录，无差异原因</div>
            <div v-else-if="!linesWithDiff.length" class="mr-note">本月盘点均无差异，无需填写差异原因</div>
            <div v-else>
              <div v-for="(item, ii) in linesWithDiff" :key="'reason' + ii" class="mr-reason-item">
                <div class="mr-reason-header">
                  <i class="el-icon-document"></i>
                  {{ item.sheetNo }} | 库位: {{ item.locationCode || '-' }} ({{ item.custName }} - {{ item.cargoName }})
                </div>
                <div class="mr-reason-detail">
                  原库存: {{ fmtNum(item.bookWeight) }}吨 → 实盘: {{ fmtNum(item.actualWeight) }}吨 &nbsp;|&nbsp;
                  <span :class="['mr-reason-diff', diffQtyClass(item.diffWeight)]">
                    差异: {{ fmtDiffQty(item.diffWeight) }}吨 ({{ fmtRatePlain(item.diffRate) }}%)
                  </span>
                </div>
                <div class="mr-reason-detail">差异原因: {{ item.diffReason || '未填写' }}</div>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="!loading" class="mr-empty">请选择月份加载报告</div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { getComplianceReport, getComplianceMonths } from './api'

const HTML2PDF_SRC =
  'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js'

export default {
  name: 'MonthlyReport',
  data() {
    return {
      yearMonth: moment().format('YYYY-MM'),
      report: null,
      loading: false,
      exporting: false,
      monthHints: []
    }
  },
  computed: {
    /** 如 2026年4月库存合规报告 */
    monthReportTitle() {
      if (!this.yearMonth) {
        return '库存合规报告'
      }
      const parts = String(this.yearMonth).split('-')
      if (parts.length < 2) {
        return '库存合规报告'
      }
      const y = parts[0]
      const m = parseInt(parts[1], 10)
      return `${y}年${m}月库存合规报告`
    },
    stdPct() {
      const p = this.report && this.report.complianceStandardPercent
      if (p == null || p === '') return '5'
      return Number(p)
    },
    /** 仅存在数量差异的明细（用于差异原因区块） */
    linesWithDiff() {
      const lines = (this.report && this.report.lines) || []
      return lines.filter((item) => {
        const d = Number(item.diffWeight)
        if (!Number.isNaN(d) && d !== 0) return true
        const b = Number(item.bookWeight != null ? item.bookWeight : 0)
        const a = Number(item.actualWeight != null ? item.actualWeight : 0)
        if (Number.isNaN(b) || Number.isNaN(a)) return false
        return Number((b - a).toFixed(4)) !== 0
      })
    },
    groupedOrders() {
      const lines = (this.report && this.report.lines) || []
      const map = new Map()
      for (let i = 0; i < lines.length; i++) {
        const row = lines[i]
        const key = (row.sheetNo || '') + '\t' + (row.countTime || '')
        if (!map.has(key)) {
          map.set(key, { sheetNo: row.sheetNo, countTime: row.countTime, rows: [] })
        }
        map.get(key).rows.push(row)
      }
      return Array.from(map.values())
    },
    /** 盘库表扁平行，便于单 v-for + rowspan */
    countRecordRows() {
      const out = []
      const groups = this.groupedOrders
      for (let gi = 0; gi < groups.length; gi++) {
        const grp = groups[gi]
        const rs = grp.rows.length
        for (let ri = 0; ri < rs; ri++) {
          out.push({
            ...grp.rows[ri],
            _sheetNo: grp.sheetNo,
            _countTime: grp.countTime,
            _rowspan: rs,
            _first: ri === 0
          })
        }
      }
      return out
    }
  },
  mounted() {
    this.bootstrapMonths()
    this.fetchReport()
  },
  methods: {
    async bootstrapMonths() {
      try {
        const res = await getComplianceMonths()
        const data = res && res.data && res.data.data
        this.monthHints = Array.isArray(data) ? data : []
        if (this.monthHints.length && this.monthHints.indexOf(this.yearMonth) < 0) {
          this.yearMonth = this.monthHints[0]
        }
      } catch (e) {
        this.monthHints = []
      }
    },
    onMonthChange() {
      this.fetchReport()
    },
    async fetchReport() {
      if (!this.yearMonth) {
        this.report = null
        return
      }
      this.loading = true
      try {
        const res = await getComplianceReport(this.yearMonth)
        const ok =
          res.data &&
          (res.data.code === 200 || res.data.success === true)
        if (ok) {
          this.report = res.data.data || null
        } else {
          this.report = null
          this.$message.warning((res.data && res.data.msg) || '加载失败')
        }
      } catch (e) {
        this.report = null
      } finally {
        this.loading = false
      }
    },
    num(v) {
      if (v == null || v === '') return 0
      const n = Number(v)
      return Number.isNaN(n) ? 0 : n
    },
    fmtNum(v) {
      return this.num(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    fmtRatePlain(v) {
      return this.num(v).toFixed(2)
    },
    fmtDiffQty(v) {
      const n = this.num(v)
      const sign = n > 0 ? '+' : ''
      return sign + n.toFixed(2)
    },
    fmtDiffRate(v) {
      const n = this.num(v)
      const sign = n > 0 ? '+' : ''
      return sign + n.toFixed(2) + '%'
    },
    diffQtyClass(v) {
      const n = this.num(v)
      if (n > 0) return 'mr-diff-pos'
      if (n < 0) return 'mr-diff-neg'
      return ''
    },
    diffRateClass(v) {
      return this.diffQtyClass(v)
    },
    loadHtml2Pdf() {
      return new Promise((resolve, reject) => {
        if (typeof window !== 'undefined' && window.html2pdf) {
          resolve(window.html2pdf)
          return
        }
        const existing = document.querySelector('script[data-html2pdf]')
        if (existing) {
          existing.addEventListener('load', () => resolve(window.html2pdf))
          existing.addEventListener('error', reject)
          return
        }
        const s = document.createElement('script')
        s.src = HTML2PDF_SRC
        s.async = true
        s.setAttribute('data-html2pdf', '1')
        s.onload = () => resolve(window.html2pdf)
        s.onerror = () => reject(new Error('html2pdf 加载失败'))
        document.head.appendChild(s)
      })
    },
    async exportPdf() {
      const el = this.$refs.reportContainer
      if (!el) {
        this.$message.warning('暂无可导出内容')
        return
      }
      this.exporting = true
      try {
        const h2p = await this.loadHtml2Pdf()
        const wrap = document.createElement('div')
        wrap.style.padding = '4px 8px 0'
        const titleEl = document.createElement('h1')
        titleEl.textContent = this.monthReportTitle
        titleEl.style.cssText =
          'text-align:center;font-size:20px;font-weight:600;margin:0 0 16px;color:#1e293b;font-family:PingFang SC,Microsoft YaHei,sans-serif'
        wrap.appendChild(titleEl)
        wrap.appendChild(el.cloneNode(true))
        const monthLabel = (this.yearMonth || '').replace('-', '年') + '月'
        const opt = {
          margin: [0.5, 0.5, 0.5, 0.5],
          filename: '库存合规报告_' + monthLabel + '.pdf',
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, letterRendering: true, useCORS: true },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
        }
        await h2p().set(opt).from(wrap).save()
      } catch (e) {
        this.$message.error('导出失败，可尝试使用浏览器打印另存为 PDF')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.mr-page {
  background: #f5f7fc;
  min-height: calc(100vh - 100px);
  padding: 24px;
  color: #1e293b;
  font-family: 'Segoe UI', 'PingFang SC', Roboto, sans-serif;
}

.mr-container {
  max-width: 1400px;
  margin: 0 auto;
}

.mr-query-band {
  margin-bottom: 24px;
  text-align: center;
}

.mr-main-title {
  margin: 0 0 16px;
  font-size: 22px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.35;
}

.mr-query-row {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.mr-query-label {
  font-size: 13px;
  color: #5b6e8c;
}

.mr-month-picker {
  width: 160px;
}

.mr-btn-export {
  border-radius: 4px;
}

.mr-stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}

@media (max-width: 900px) {
  .mr-stats-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

.mr-stat-card {
  background: #f8fafc;
  border-radius: 20px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  text-align: center;
}

.mr-stat-label {
  font-size: 13px;
  color: #5b6e8c;
  margin-bottom: 8px;
}

.mr-stat-value {
  font-size: 28px;
  font-weight: 700;
}

.mr-diff-pos {
  color: #10b981;
}

.mr-diff-neg {
  color: #ef4444;
}

.mr-compliance-card {
  background: linear-gradient(135deg, #f0f9ff 0%, #e6f0ff 100%);
  border-left: 4px solid #3b82f6;
  margin-bottom: 24px;
  padding: 20px;
  border-radius: 20px;
}

.mr-compliance-title {
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.mr-compliance-content {
  font-size: 14px;
  line-height: 1.5;
}

.mr-pass {
  color: #10b981;
  font-weight: 600;
}

.mr-fail {
  color: #ef4444;
  font-weight: 600;
}

.mr-compliance-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #5b6e8c;
}

.mr-card {
  background: #fff;
  border-radius: 24px;
  padding: 24px;
  border: 1px solid #e9eef3;
  margin-bottom: 24px;
}

.mr-card-header {
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 20px;
  border-left: 3px solid #3b82f6;
  padding-left: 12px;
}

.mr-table-wrap {
  overflow-x: auto;
}

.mr-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.mr-table th,
.mr-table td {
  text-align: left;
  padding: 12px 8px;
  border-bottom: 1px solid #eef2f6;
  vertical-align: top;
}

.mr-table th {
  background: #f8fafc;
  font-weight: 600;
}

.mr-td-center {
  text-align: center;
  color: #8ba0bc;
}

.mr-note {
  font-size: 12px;
  color: #8ba0bc;
  margin-top: 12px;
  text-align: center;
}

.mr-reason-item {
  background: #f8fafc;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 8px;
  border-left: 3px solid #3b82f6;
}

.mr-reason-header {
  font-weight: 500;
  margin-bottom: 6px;
}

.mr-reason-detail {
  font-size: 12px;
  color: #5b6e8c;
}

.mr-reason-diff {
  font-weight: 600;
}

.mr-empty {
  text-align: center;
  padding: 48px;
  color: #8ba0bc;
}

@media print {
  .no-print,
  .mr-query-band {
    display: none !important;
  }

  .mr-page {
    padding: 0;
    background: #fff;
  }

  .mr-card,
  .mr-compliance-card {
    page-break-inside: avoid;
  }
}
</style>
